import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserStore } from '@Auth/store';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from '@components/ui/empty';
import type { SearchResult } from '@responses/search';
import { SearchXIcon } from 'lucide-react';

import { SearchResultsSkeleton } from '../components/SearchResultsSkeleton';
import { useCommandPaletteSearch } from '../hooks/useCommandPaletteSearch';
import { useContractNavigation } from '../hooks/useContractNavigation';
import { useRecentStorage } from '../hooks/useRecentStorage';
import type { SearchResultType, SearchSort } from '../types';
import { getFirstMatchedBlockIds } from '../utils/flatten-highlights';
import { getHighlightTerms } from '../utils/get-highlight-terms';
import { getResultLink } from '../utils/get-result-link';
import { SearchResults } from '../views/SearchResults';

export const SearchResultsController = ({
  query,
  type,
  debouncedQuery,
  hasQuery,
  sort,
  isOpen,
  setItemCount,
  setTotalItemCount,
}: {
  query: string;
  type?: SearchResultType;
  debouncedQuery: string;
  hasQuery: boolean;
  sort: SearchSort;
  isOpen: boolean;
  setItemCount: (count: number) => void;
  setTotalItemCount: (count: number) => void;
}) => {
  'use memo';

  const { searchResults, total, isError, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCommandPaletteSearch({
      query,
      debouncedQuery,
      hasQuery,
      type,
      sort,
      enabled: isOpen,
    });

  const showNoResults = hasQuery && !isPending && !isError && searchResults.length === 0;

  useEffect(() => {
    setItemCount(searchResults.length);
    setTotalItemCount(total);
    return () => {
      setItemCount(0);
      setTotalItemCount(0);
    };
  }, [searchResults.length, total, setItemCount, setTotalItemCount]);

  const user = useUserStore(({ user }) => user);
  const { addRecentSearch, addRecentContract } = useRecentStorage(user?.username ?? '');

  const goTo = useContractNavigation();

  const parentRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const onSelectItem = useCallback(
    (item: SearchResult, openInNewTab: boolean) => {
      addRecentSearch(query);
      if (item.type === 'submissions') {
        addRecentContract({
          id: item.id,
          name: item.name ?? '',
          ref: item.ref,
          status: item.status,
        });
      }
      const highlightTerms = getHighlightTerms(item.highlights, query);
      goTo(getResultLink(item.type, item.id, getFirstMatchedBlockIds(item.highlights), highlightTerms), openInNewTab);
    },
    [addRecentSearch, addRecentContract, goTo, query],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [isOpen, query, type, sort]);

  useEffect(() => {
    document.getElementById(`command-palette-item-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (total === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (searchResults.length === 0) {
          return;
        }
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((current) => (current + delta + searchResults.length) % searchResults.length);
        return;
      }

      if (event.key === 'Enter' && searchResults[activeIndex]) {
        event.preventDefault();
        onSelectItem(searchResults[activeIndex], event.metaKey || event.ctrlKey);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, total, searchResults, onSelectItem]);

  if (isError) {
    return <div className="text-destructive px-3 py-8 text-center text-sm">Search failed — please try again.</div>;
  }

  if (showNoResults) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="default" className="text-muted-foreground">
            <SearchXIcon aria-hidden />
          </EmptyMedia>
          <EmptyDescription>No results for “{query.trim()}”</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return isPending && !isFetchingNextPage ? (
    <SearchResultsSkeleton />
  ) : (
    <SearchResults
      parentRef={parentRef}
      searchResults={searchResults}
      hasNextPage={hasNextPage}
      showUpdatedAt={sort === 'recent'}
      activeIndex={activeIndex}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      setActiveIndex={setActiveIndex}
      onSelectItem={onSelectItem}
    />
  );
};
